import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IconInfoCircle, IconEyeOff } from "@tabler/icons-react";
import MHeader from "@/components/ui/organisms/MHeader";
import MLoader from "@/components/ui/atoms/MLoader";
import MCheckbox from "@/components/ui/atoms/MCheckbox";
import { MButton } from "@/components/ui/atoms/MButtons";
import MDialog from "@/components/ui/atoms/MDialog";
import FooterDrawer from "@/components/ui/atoms/FooterDrawer";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { getMemberInfo } from "@/hooks/useMembers";
import { useFamily } from "@/hooks/useFamily";
import { useMemberPermission } from "@/hooks/useMemberPermission";
import { usePendingAccessRequests } from "@/hooks/usePendingAccessRequests";
import memberPermissionService from "@/services/member-permissions.service";
import { memberPermissionsService } from "@/services/memberPermissions.service";
import { membersService } from "@/services/members.service";
import { isChild, GenderType } from "@/types/member";
import { trackUserDataLoaded } from "@/utils/gtm";
import {
  ANALYTICS_SCREEN_TYPES,
  ANALYTICS_SCREEN_NAMES,
} from "@/constants/analytics.constants";
import AccessMemberRow from "./AccessMemberRow";
import styles from "./ManageMyAccess.module.css";

type TabKey = "seen" | "seeing";

interface AccessItem {
  member: any;
  hasAccess: boolean;
  consentPending?: boolean;
  checkedAccess?: boolean;
}

const LEARN_MORE_CONTENT = (
  <>
    You decide who sees your health information, and you can ask other members
    to share theirs with you.
    <br />
    <br />
    Anyone with access can view or update the health records shared with them.
    Only members with a Maatra account can share or be given access.
  </>
);

const ManageMyAccess: React.FC = () => {
  const memberInfo = getMemberInfo(true);
  const toast = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const selectedMember = location.state?.selectedMember;
  const initialTab: TabKey =
    location.state?.initialTab === "seeing" ? "seeing" : "seen";
  const [activeTab, setActiveTab] = useState<TabKey>(initialTab);

  const { data: familyMembers = [], managedIds } = useFamily();
  const isViewingOwnProfile = selectedMember?.id === memberInfo.id;
  const isLoggedInUserFO = memberInfo.family_member_type === "fo";
  const isTargetRegistered = !!selectedMember?.registered_member;
  const targetName =
    selectedMember?.nickname || selectedMember?.first_name || "this member";
  const pronoun =
    selectedMember?.gender === GenderType.MALE
      ? { subject: "he", possessive: "his", sees: "sees", has: "has" }
      : selectedMember?.gender === GenderType.FEMALE
      ? { subject: "she", possessive: "her", sees: "sees", has: "has" }
      : { subject: "they", possessive: "their", sees: "see", has: "have" };

  const handleSendInvite = () => {
    navigate("/profile/family/invite-contact-method", {
      state: {
        targetMember: selectedMember,
        backUrl: window.location.pathname,
      },
    });
  };

  const consentPendingContent = isViewingOwnProfile ? (
    <>
      Notification sent to this family member to access your profile.
      <br />
      <br />
      They&rsquo;ll be able to access the profile once they give consent.
    </>
  ) : (
    <>
      Notification sent to this family member to access profile.
      <br />
      <br />
      They&rsquo;ll be able to access the profile once they give consent.
    </>
  );

  useEffect(() => {
    if (!memberInfo?.id) return;
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.FAMILY,
      screenName: isViewingOwnProfile
        ? ANALYTICS_SCREEN_NAMES.FAMILY_MEMBER_MANAGE
        : ANALYTICS_SCREEN_NAMES.FAMILY_OWNER_MANAGE,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberInfo?.id]);

  const { handleGrantAccess, loading: grantLoading } = useMemberPermission(
    selectedMember?.id,
    selectedMember?.current_family
  );

  /* ---------------------------------------------------------------------- */
  /* "Profiles you see" tab — profiles selectedMember can access             */
  /* ---------------------------------------------------------------------- */
  const [seenAccess, setSeenAccess] = useState<AccessItem[]>([]);
  const [seenTokens, setSeenTokens] = useState<any[]>([]);
  const [seenLoading, setSeenLoading] = useState(true);
  const [seenSubmitting, setSeenSubmitting] = useState(false);
  const [showConsentDialog, setShowConsentDialog] = useState(false);
  const [requestTargetMember, setRequestTargetMember] = useState<any>(null);
  const [requestingAccess, setRequestingAccess] = useState(false);
  const { pendingRequests } = usePendingAccessRequests({
    familyMembers,
    managedIds,
    familyMemberType: memberInfo.family_member_type,
    enabled: memberInfo.family_member_type === "fm",
  });

  useEffect(() => {
    async function fetchSeenAccess() {
      if (!selectedMember || familyMembers.length === 0) return;
      setSeenLoading(true);
      try {
        const managedMembers = await membersService.getManagedMembersForMember(
          selectedMember.id
        );
        const managedMemberIds = managedMembers.map((m: any) => m.id || m._id);
        const { tokens } =
          await memberPermissionsService.getPendingAccessTokensForMember(
            selectedMember.id
          );
        setSeenTokens(tokens || []);
        setSeenAccess(
          familyMembers
            .filter((m: any) => m.id !== selectedMember.id)
            .map((m: any) => {
              const consentPending = tokens?.some(
                (t: any) => t.token_params?.familyMemberId === m.id
              );
              const hasAccess = managedMemberIds.includes(m.id);
              return {
                member: m,
                hasAccess,
                consentPending,
                checkedAccess: hasAccess || consentPending,
              };
            })
        );
      } finally {
        setSeenLoading(false);
      }
    }
    fetchSeenAccess();
  }, [selectedMember?.id, familyMembers]);

  const handleSeenToggle = (memberId: string) => {
    setSeenAccess((prev) =>
      prev.map((item) =>
        item.member.id === memberId
          ? { ...item, checkedAccess: !item.checkedAccess }
          : item
      )
    );
  };

  const handleRequestAccess = (member: any) => {
    setRequestTargetMember(member);
    setShowConsentDialog(true);
  };

  const handleConfirmRequestAccess = async () => {
    if (!requestTargetMember) return;
    setRequestingAccess(true);
    try {
      await memberPermissionsService.requestAccess({
        memberId: selectedMember.id,
        familyId: selectedMember.current_family,
        familyMemberId: requestTargetMember.id,
      });
      toast.showToast({
        message: "Access request sent to Family Owner",
        variant: "default",
      });
      setShowConsentDialog(false);
      setRequestTargetMember(null);
      globalThis.window?.location.reload();
    } catch {
      toast.showToast({
        message: "Failed to send access request",
        variant: "error",
      });
      setRequestingAccess(false);
    }
  };

  const handleSeenDone = async () => {
    setSeenSubmitting(true);
    const revokePromises = seenAccess
      .filter((item) => {
        const consentPending = seenTokens.some(
          (t) => t.token_params?.familyMemberId === item.member.id
        );
        const wasChecked = item.hasAccess || consentPending;
        return wasChecked && !item.checkedAccess;
      })
      .map((item) => {
        const consentPending = seenTokens.some(
          (t) => t.token_params?.familyMemberId === item.member.id
        );
        if (consentPending) {
          const tokenObj = seenTokens.find(
            (t) => t.token_params?.familyMemberId === item.member.id
          );
          return memberPermissionsService.revokeInvite({
            memberId: selectedMember.id,
            familyId: selectedMember.current_family,
            familyMemberId: item.member.id,
            token: tokenObj?.token,
          });
        } else if (item.hasAccess) {
          return memberPermissionsService.revokeAccess({
            memberId: selectedMember.id,
            familyId: selectedMember.current_family,
            familyMemberId: item.member.id,
          });
        }
        return Promise.resolve();
      });
    const grantedMembers = seenAccess.filter(
      (item) => !item.hasAccess && item.checkedAccess
    );
    const grantPromises = grantedMembers.map((item) => {
      const skipConsent = item.member.member_type === "p";
      return handleGrantAccess(item.member.id, selectedMember.id, skipConsent);
    });
    const hasInvites =
      grantedMembers.some((item) => item.member.member_type !== "p") &&
      grantPromises.length > 0;
    const hasRevokes = revokePromises.length > 0;
    try {
      await Promise.all([...revokePromises, ...grantPromises]);
      toast.showToast({
        message: hasInvites
          ? "Access grant notification sent"
          : hasRevokes
          ? "Access removal notification sent"
          : "Access grant notification sent",
        variant: "default",
      });
      navigate(-1);
    } catch {
      toast.showToast({ message: "Please try again.", variant: "error" });
    } finally {
      setSeenSubmitting(false);
    }
  };

  const seenHasChanges = seenAccess.some((item) => {
    const wasChecked = item.hasAccess || item.consentPending;
    return wasChecked !== item.checkedAccess;
  });

  /* ---------------------------------------------------------------------- */
  /* "Who sees you" tab — who can access selectedMember's profile            */
  /* ---------------------------------------------------------------------- */
  const [seeingAccess, setSeeingAccess] = useState<AccessItem[]>([]);
  const [seeingTokens, setSeeingTokens] = useState<any[]>([]);
  const [seeingLoading, setSeeingLoading] = useState(true);
  const [seeingSubmitting, setSeeingSubmitting] = useState(false);

  useEffect(() => {
    async function fetchSeeingAccess() {
      if (!selectedMember || familyMembers.length === 0) return;
      setSeeingLoading(true);
      try {
        const { ids, tokens } =
          await memberPermissionService.getManagedMemberIdsAndTokens(
            selectedMember.id
          );
        setSeeingTokens(tokens || []);
        setSeeingAccess(
          familyMembers
            .filter(
              (m: any) => m.id !== selectedMember.id && m.registered_member
            )
            .map((m: any) => {
              const consentPending = tokens?.some(
                (t: any) => t.member_id === m.id
              );
              const hasAccess =
                ids?.includes(m.id) || m.family_member_type === "fo";
              return {
                member: m,
                hasAccess,
                consentPending,
                checkedAccess: hasAccess || consentPending,
              };
            })
        );
      } finally {
        setSeeingLoading(false);
      }
    }
    fetchSeeingAccess();
  }, [selectedMember?.id, familyMembers]);

  const handleSeeingToggle = (memberId: string) => {
    setSeeingAccess((prev) =>
      prev.map((item) =>
        item.member.id === memberId
          ? { ...item, checkedAccess: !item.checkedAccess }
          : item
      )
    );
  };

  const handleSeeingDone = async () => {
    setSeeingSubmitting(true);
    const skipConsent = selectedMember?.member_type === "p";
    const revokePromises = seeingAccess
      .filter((item) => {
        const consentPending = seeingTokens.some(
          (t) => t.member_id === item.member.id
        );
        const wasChecked = item.hasAccess || consentPending;
        return (
          wasChecked &&
          !item.checkedAccess &&
          item.member.family_member_type !== "fo"
        );
      })
      .map((item) => {
        const consentPending = seeingTokens.some(
          (t) => t.member_id === item.member.id
        );
        if (consentPending) {
          const tokenObj = seeingTokens.find(
            (t) => t.member_id === item.member.id
          );
          return memberPermissionService.revokeInvite({
            memberId: item.member.id,
            familyId: selectedMember.current_family,
            familyMemberId: selectedMember.id,
            token: tokenObj?.token,
          });
        } else if (item.hasAccess) {
          return memberPermissionService.revokeAccess({
            memberId: item.member.id,
            familyId: selectedMember.current_family,
            familyMemberId: selectedMember.id,
          });
        }
        return Promise.resolve();
      });
    const grantPromises = seeingAccess
      .filter((item) => {
        const consentPending = seeingTokens.some(
          (t) => t.member_id === item.member.id
        );
        const wasChecked = item.hasAccess || consentPending;
        return (
          !wasChecked &&
          item.checkedAccess &&
          item.member.family_member_type !== "fo"
        );
      })
      .map((item) =>
        handleGrantAccess(selectedMember.id, item.member.id, skipConsent)
      );
    const hasInvites = !skipConsent && grantPromises.length > 0;
    const hasRevokes = revokePromises.length > 0;
    try {
      await Promise.all([...revokePromises, ...grantPromises]);
      toast.showToast({
        message: hasInvites
          ? "Access grant notification sent"
          : hasRevokes
          ? "Access removal notification sent"
          : "Access grant notification sent",
        variant: "default",
      });
      navigate(-1);
    } catch {
      toast.showToast({ message: "Please try again.", variant: "error" });
    } finally {
      setSeeingSubmitting(false);
    }
  };

  const seeingHasChanges = seeingAccess.some((item) => {
    if (item.member.family_member_type === "fo") return false;
    const wasChecked = item.hasAccess || item.consentPending;
    return wasChecked !== item.checkedAccess;
  });

  /* ---------------------------------------------------------------------- */
  /* Shared UI state                                                        */
  /* ---------------------------------------------------------------------- */
  const [consentPendingInfoDrawerOpen, setConsentPendingInfoDrawerOpen] =
    useState(false);
  const [confirmChecked, setConfirmChecked] = useState(false);

  if (!selectedMember) return <MLoader />;

  const isMemberChild =
    selectedMember.member_type === "h" &&
    isChild({
      dob_year: selectedMember.dob_year,
      dob_month: selectedMember.dob_month,
      dob_day: selectedMember.dob_day,
    });
  const registeredMemberCount = familyMembers.filter(
    (m: any) => m.registered_member
  ).length;

  const loading =
    grantLoading || (activeTab === "seen" ? seenLoading : seeingLoading);
  const submitting = activeTab === "seen" ? seenSubmitting : seeingSubmitting;
  const hasChanges = activeTab === "seen" ? seenHasChanges : seeingHasChanges;

  const showFooter =
    registeredMemberCount > 1 &&
    !(activeTab === "seen" && isViewingOwnProfile && isLoggedInUserFO);

  const getRowName = (member: any) =>
    member.id === memberInfo.id
      ? "You"
      : `${member.nickname || member.first_name}`.trim();

  const renderSeenRow = (item: AccessItem) => {
    const name = getRowName(item.member);
    const isOwner = item.member.family_member_type === "fo";

    if (isLoggedInUserFO && isViewingOwnProfile) {
      return (
        <AccessMemberRow
          key={item.member.id}
          name={name}
          isOwner={isOwner}
          right={{
            type: "checkbox",
            checked: true,
            disabled: true,
            onChange: () => {},
          }}
        />
      );
    }

    const isFMManagingFO =
      !isLoggedInUserFO && selectedMember.family_member_type === "fo";
    const shouldDisableCheckbox = seenLoading || isFMManagingFO;
    const showAskButton =
      !isLoggedInUserFO && !item.hasAccess && !item.consentPending;

    return (
      <AccessMemberRow
        key={item.member.id}
        name={name}
        isOwner={isOwner}
        right={
          showAskButton
            ? {
                type: "button",
                label: pendingRequests[item.member.id]
                  ? "Access requested"
                  : "Ask for access",
                disabled: pendingRequests[item.member.id],
                onClick: () => handleRequestAccess(item.member),
              }
            : {
                type: "checkbox",
                checked: !!item.checkedAccess,
                disabled: shouldDisableCheckbox,
                onChange: () => handleSeenToggle(item.member.id),
              }
        }
      />
    );
  };

  const renderSeeingRow = (item: AccessItem) => {
    const name = getRowName(item.member);
    const isOwner = item.member.family_member_type === "fo";
    const disabled = isOwner || seeingLoading || item.consentPending;

    return (
      <AccessMemberRow
        key={item.member.id}
        name={name}
        isOwner={isOwner}
        consentPending={item.consentPending}
        onConsentInfoClick={() => setConsentPendingInfoDrawerOpen(true)}
        right={{
          type: "checkbox",
          checked: !!item.checkedAccess,
          disabled,
          onChange: () => handleSeeingToggle(item.member.id),
        }}
      />
    );
  };

  const seenList = [...seenAccess].sort((a, b) => {
    if (a.member.family_member_type === "fo") return -1;
    if (b.member.family_member_type === "fo") return 1;
    return 0;
  });
  const seeingList = [...seeingAccess].sort((a, b) => {
    if (a.member.family_member_type === "fo") return -1;
    if (b.member.family_member_type === "fo") return 1;
    return 0;
  });
  // An unregistered member has no Maatra account, so they can never have
  // access to anyone else's profile — the "seen" tab always shows the
  // "isn't on Maatra yet" empty state regardless of fetched data.
  const showUnregisteredEmptyState =
    activeTab === "seen" && !isViewingOwnProfile && !isTargetRegistered;
  const isEmpty =
    showUnregisteredEmptyState ||
    (activeTab === "seen" ? seenList : seeingList).length === 0;

  return (
    <div className={styles.container}>
      <div className={styles.headerWrapper}>
        <MHeader
          module="Family"
          memberInfo={memberInfo}
          className={styles.header}
          disableSticky
          title={
            isViewingOwnProfile
              ? "Manage your access"
              : `Manage ${targetName}'s access`
          }
          description={
            isLoggedInUserFO && isViewingOwnProfile
              ? "As the Family Owner, you have access to everyone's profile. Choose who can see yours."
              : isViewingOwnProfile
              ? "Choose whose profile you can see and who can see yours."
              : "Choose whose profile they can see and who can see theirs."
          }
          learnMoreContent={LEARN_MORE_CONTENT}
          drawerTitle="Learn more"
          hideBack
        />

        <div className={styles.tabsWrapper}>
          <div className={styles.tabs}>
            <div className={styles.tabButtonGroup}>
              <button
                type="button"
                className={`${styles.tabButton} ${
                  activeTab === "seen" ? styles.tabButtonActive : ""
                }`}
                onClick={() => setActiveTab("seen")}
              >
                {isViewingOwnProfile
                  ? "Profiles you see"
                  : `Profiles ${targetName} sees`}
              </button>
              <button
                type="button"
                className={`${styles.tabButton} ${
                  activeTab === "seeing" ? styles.tabButtonActive : ""
                }`}
                onClick={() => setActiveTab("seeing")}
              >
                {isViewingOwnProfile
                  ? "Who sees you"
                  : `Who sees ${targetName}'s`}
              </button>
            </div>
          </div>
        </div>
      </div>

      {!isEmpty && (
        <div className={styles.infoBanner}>
          <IconInfoCircle size={24} className={styles.infoBannerIcon} />
          <p className={styles.infoBannerText}>
            {activeTab === "seen"
              ? isViewingOwnProfile
                ? "A tick means you can see that profile. Untick to remove access anytime."
                : `A tick means ${targetName} can see that profile. Untick to remove access anytime.`
              : isViewingOwnProfile
              ? "A tick means they can see your profile. Untick to remove access anytime."
              : `A tick means they can see ${targetName}'s profile. Untick to remove access anytime.`}
          </p>
        </div>
      )}

      {loading ? (
        <MLoader />
      ) : isEmpty ? (
        <div className={styles.memberList}>
          <div className={styles.emptyCard}>
            <div className={styles.emptyStateMessage}>
              <IconEyeOff size={53} className={styles.emptyStateIcon} />
              <p className={styles.emptyStateTitle}>
                {showUnregisteredEmptyState
                  ? `${targetName} isn't on Maatra yet`
                  : activeTab === "seen"
                  ? "No profiles to see yet"
                  : "No family member yet"}
              </p>
              <p className={styles.emptyStateSubtitle}>
                {showUnregisteredEmptyState
                  ? `You can choose which profiles ${pronoun.subject} ${pronoun.sees} once ${pronoun.subject} ${pronoun.has} ${pronoun.possessive} own account.`
                  : activeTab === "seen"
                  ? "Add a family member to start managing their health details."
                  : "Add a family member to share your profile with them."}
              </p>
            </div>
            <MButton
              variant="filled"
              size="sm"
              onClick={
                showUnregisteredEmptyState
                  ? handleSendInvite
                  : () => navigate("/profile/family/add-member")
              }
              className={styles.emptyStateButton}
            >
              {showUnregisteredEmptyState
                ? "+ Send invite"
                : "+ Add a family member"}
            </MButton>
          </div>
        </div>
      ) : (
        <div className={styles.memberList}>
          {activeTab === "seen"
            ? seenList.map(renderSeenRow)
            : seeingList.map(renderSeeingRow)}
        </div>
      )}

      {isMemberChild && registeredMemberCount > 1 && (
        <div className="flex flex-row items-center gap-2 mt-8 mb-6 px-6">
          <MCheckbox
            checked={confirmChecked}
            onChange={() => setConfirmChecked((v) => !v)}
            id="confirm-checkbox"
          />
          <label
            htmlFor="confirm-checkbox"
            className="body-base-medium default-color"
            style={{ cursor: "pointer", marginLeft: 16 }}
          >
            I confirm that the selected adults are the child&rsquo;s parents or
            legal guardians.
          </label>
        </div>
      )}

      {showFooter && (
        <div className={styles.confirmFooter}>
          <MButton
            variant="filled"
            className={styles.confirmButton}
            onClick={activeTab === "seen" ? handleSeenDone : handleSeeingDone}
            disabled={
              submitting || !hasChanges || (isMemberChild && !confirmChecked)
            }
          >
            Confirm
          </MButton>
        </div>
      )}

      <MDialog
        isOpen={showConsentDialog}
        onClose={() => {
          setShowConsentDialog(false);
          setRequestTargetMember(null);
        }}
        title="Consent needed"
        description={`By continuing, you confirm that ${
          requestTargetMember?.nickname ||
          requestTargetMember?.first_name ||
          "this family member"
        } has consented to share their health details with you.`}
        primaryCtaText="Confirm"
        secondaryCtaText="Cancel"
        onPrimaryCta={handleConfirmRequestAccess}
        onSecondaryCta={() => {
          setShowConsentDialog(false);
          setRequestTargetMember(null);
        }}
        isPrimaryCtaLoading={requestingAccess}
      />

      <FooterDrawer
        open={consentPendingInfoDrawerOpen}
        onClose={() => setConsentPendingInfoDrawerOpen(false)}
        title="Consent pending"
      >
        <div className="accessRequestText">{consentPendingContent}</div>
      </FooterDrawer>
    </div>
  );
};

export default ManageMyAccess;
